// @flow
import { Auth } from 'aws-amplify';
import moment from 'moment';
import { sessionST } from 'lib/sessionStorage';
import { createMemoFn } from 'lib/propHelpers';
import queryString from 'query-string';
import { SSO_ONLY_DOMAINS, PROVIDERS_CONFIG } from './config';
import { ProviderConfig } from './types.js.flow';
import { storage } from 'lib/storage';

type TFederatedSignIn = {
  backUrl?: string,
};

export const ssoEventErrorMessages = {
  invalid_grant: 'invalid_grant',
  default: 'something_wrong',
};

export const getAuthProvider = (config: ProviderConfig = PROVIDERS_CONFIG) => {
  const { host } = window.location;
  return Object.entries(config).reduce(
    (res, [provider, hosts]) => (!res && hosts.includes(host) ? provider : res),
    null,
  );
};

export const federatedSignIn: TFederatedSignIn = (backUrl = '') => {
  if (backUrl) {
    storage().backUrl().set(backUrl);
  }
  const provider = getAuthProvider();
  if (provider) {
    Auth.federatedSignIn({ provider });
  } else {
    Auth.federatedSignIn();
  }
};

export const isAuthenticated = async () => {
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch {
    return false;
  }
};

// checks if token is expired and refreshes with Cognito if needed automatically
export const getCognitoToken = async () => {
  const session = await Auth.currentSession();
  return session.getAccessToken().getJwtToken();
};

export const persistTokenRefreshTime = (timestamp: number | null) => {
  sessionST().cognitoTokenLastRefreshAt().set(timestamp);
};

export const nullifyTokenRefreshTime = sessionST().cognitoTokenLastRefreshAt().remove;

export const getTokenRefreshTime = (): number | null => {
  const value = sessionST().cognitoTokenLastRefreshAt().get();

  if (value) return parseInt(value, 10);
  return null;
};

export const tokenInctivityTimeoutReached = (timestamp: number | null) => {
  if (timestamp === null) return false;

  const updatedAt = moment(timestamp);
  return updatedAt.isBefore(moment().subtract(process.env.REACT_APP_COGNITO_INACTIVITY_TIMEOUT || 8, 'hours'));
};

export const checkForTokenInactivityTimeout = () => {
  const tokenLastRefreshTime = getTokenRefreshTime();
  const inactivityLimitReached = tokenInctivityTimeoutReached(tokenLastRefreshTime);
  if (inactivityLimitReached) {
    // cleanup previous data not to force subsecuent getToken requests to throw again
    // until logout action redirects to login
    nullifyTokenRefreshTime();
    throw new Error('Token inactivity timeout reached');
  }
};

export const isDomainSSOOnly = createMemoFn(
  () => ({ host: window.location.host }),
  ({ host }) => SSO_ONLY_DOMAINS.includes(host),
);

export const isRedirectFromSSO = createMemoFn(
  () => ({ search: window.location.search }),
  ({ search }) => {
    const queryParams = queryString.parse(search);
    return queryParams.code;
  },
);

export const getRedirectFromAuth0Error = createMemoFn(
  () => ({ search: window.location.search }),
  ({ search }) => {
    const queryParams = queryString.parse(search);
    return queryParams.error_description || queryParams.error || null;
  },
);

export const getSsoEventMessageKey = (eventName: string) =>
  ssoEventErrorMessages[eventName] || ssoEventErrorMessages.default;
